'use client';

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Check, Loader2 } from 'lucide-react';

import { toast } from 'sonner';
import { ApiError } from '@/lib/api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormTextarea } from '@/components/forms/form-textarea';
import { FileUploader } from '@/components/file-uploader';
import { useUploadProgress } from '@/hooks/use-upload-progress';
import { Label } from '@/components/ui/label';
import { PasswordStrengthIndicator } from '@/features/auth';
import {
  createClientSchema,
  updateClientSchema,
  type ClientFormValues,
  type ClientUpdateFormValues
} from '../schemas';
import {
  useCreateClientMutation,
  useUpdateClientMutation
} from '../services/clients.mutations';
import type { Client } from '../types';

interface ClientFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client | null;
}

export function ClientFormDialog({
  open,
  onOpenChange,
  client
}: ClientFormDialogProps) {
  const t = useTranslations('clients');
  const tv = useTranslations('validation');
  const isEditing = !!client;

  const schema = useMemo(
    () => (isEditing ? updateClientSchema(tv) : createClientSchema(tv)),
    [tv, isEditing]
  );

  const form = useForm<ClientFormValues | ClientUpdateFormValues>({
    resolver: zodResolver(schema) as any,
    mode: 'onChange',
    defaultValues: {
      name: '',
      admin_email: '',
      password: '',
      confirm_password: '',
      notes: '',
      logo: null
    }
  });

  const createMutation = useCreateClientMutation();
  const updateMutation = useUpdateClientMutation();
  const { progress, onProgress, reset } = useUploadProgress();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const passwordValue = form.watch('password') ?? '';
  const logoValue = form.watch('logo') ?? null;

  useEffect(() => {
    if (open) {
      if (client) {
        form.reset({
          name: client.name,
          admin_email: client.admin_email,
          password: '',
          confirm_password: '',
          notes: client.notes ?? '',
          logo: null
        });
      } else {
        form.reset({
          name: '',
          admin_email: '',
          password: '',
          confirm_password: '',
          notes: '',
          logo: null
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client]);

  async function onSubmit(values: ClientFormValues | ClientUpdateFormValues) {
    try {
      if (isEditing && client) {
        await updateMutation.mutateAsync({
          id: client.id,
          data: {
            name: values.name.trim(),
            admin_email: values.admin_email.trim(),
            password: values.password || undefined,
            notes: values.notes?.trim() || undefined,
            logo: values.logo ?? null
          },
          onProgress
        });
      } else {
        await createMutation.mutateAsync({
          name: values.name.trim(),
          admin_email: values.admin_email.trim(),
          password: values.password!,
          notes: values.notes?.trim() || undefined,
          logo: values.logo ?? null,
          onProgress
        });
      }
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        const fieldErrors = (
          error.data as { errors?: Record<string, string[]> }
        )?.errors;
        if (fieldErrors) {
          Object.entries(fieldErrors).forEach(([field, messages]) => {
            form.setError(field as Parameters<typeof form.setError>[0], {
              message: messages[0]
            });
          });
        }
      } else {
        toast.error(
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred.'
        );
      }
    } finally {
      reset();
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='!grid-rows-[auto_1fr_auto] overflow-hidden sm:max-h-[90vh] sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('formTitleEdit') : t('formTitle')}
          </DialogTitle>
        </DialogHeader>

        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className='min-h-0 overflow-y-auto'
        >
          <div className='grid gap-4 px-1 py-4'>
            <div className='space-y-2'>
              <Label>{t('logoLabel')}</Label>
              <FileUploader
                value={logoValue ? [logoValue] : []}
                progresses={
                  logoValue && progress !== null
                    ? { [logoValue.name]: progress }
                    : undefined
                }
                onValueChange={(files) => {
                  form.setValue(
                    'logo',
                    Array.isArray(files) ? (files[0] ?? null) : null,
                    { shouldValidate: true }
                  );
                }}
                accept={{
                  'image/svg+xml': ['.svg'],
                  'image/png': ['.png'],
                  'image/jpeg': ['.jpg', '.jpeg']
                }}
                maxSize={5 * 1024 * 1024}
                maxFiles={1}
              />
              {form.formState.errors.logo && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.logo.message}
                </p>
              )}
              <p className='text-muted-foreground text-xs'>
                {t('logoDescription')}
              </p>
            </div>

            <FormInput
              control={form.control}
              name='name'
              label={t('nameLabel')}
              placeholder={t('namePlaceholder')}
              required
            />

            <FormInput
              control={form.control}
              name='admin_email'
              type='email'
              label={t('adminEmailLabel')}
              placeholder={t('adminEmailPlaceholder')}
              required
            />

            <div className='space-y-2'>
              <FormInput
                control={form.control}
                name='password'
                type='password'
                label={t('passwordLabel')}
                placeholder={t('passwordPlaceholder')}
                required={!isEditing}
              />
              {passwordValue && (
                <PasswordStrengthIndicator password={passwordValue} />
              )}
            </div>

            <FormInput
              control={form.control}
              name='confirm_password'
              type='password'
              label={t('confirmPasswordLabel')}
              placeholder={t('confirmPasswordPlaceholder')}
              required={!isEditing}
            />

            <FormTextarea
              control={form.control}
              name='notes'
              label={t('notesLabel')}
              placeholder={t('notesPlaceholder')}
            />
          </div>
        </Form>

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            type='submit'
            disabled={isPending || !form.formState.isValid}
            onClick={form.handleSubmit(onSubmit)}
          >
            {isPending ? (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            ) : (
              <Check className='me-2 h-4 w-4' />
            )}
            {isEditing ? t('updateClient') : t('saveClient')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
